import { Link } from "react-router";

import { EmptyState } from "../lib/EmptyState.tsx";
import { WorkThumb } from "../lib/WorkThumb.tsx";
import { ARRANGEMENTS_COPY } from "./arrangements.constants.ts";
import { useArrangementsController } from "./useArrangementsController.ts";
import type { ArrangementRow } from "./arrangements.model.ts";
import "./arrangements.css";

/**
 * What is out, across every work the reader owns.
 *
 * The collector's "Loans, Consign & Agents", built. Two groups off one read —
 * the works an agent represents and the works a gallery holds — and a plain
 * statement of what the screen does NOT record, so the rail's word "loans"
 * reads as a gap and not as an empty ledger.
 *
 * IT READS THE TABLE, NEVER A FEED. What is out is a "what is true" question,
 * and CLAUDE.md is explicit that a screen answering one from a feed is a bug.
 *
 * It carries no title of its own: the portal header above it does.
 */
export function Arrangements({ workBase }: { workBase: string }) {
  const { view } = useArrangementsController(workBase);

  if (view.kind === "loading") {
    return (
      <p className="arrangements__note" role="status">
        {view.message}
      </p>
    );
  }

  if (view.kind === "error") {
    return (
      <section className="arrangements__note arrangements__note--error" role="alert">
        <h2 className="arrangements__note-title">{view.title}</h2>
        <p>{view.message}</p>
      </section>
    );
  }

  if (view.kind === "empty") {
    return (
      <>
        <EmptyState className="arrangements__empty" lead={view.message} detail={view.detail} />
        <NotHere />
      </>
    );
  }

  return (
    <>
      <p className="arrangements__summary">{view.summary}</p>

      <section className="arrangements__lead">
        <p>
          <strong>{ARRANGEMENTS_COPY.lead}</strong> {ARRANGEMENTS_COPY.leadDetail}
        </p>
      </section>

      {view.mandates.length > 0 && (
        <Group
          title={ARRANGEMENTS_COPY.groups.mandates}
          note={ARRANGEMENTS_COPY.groups.mandatesNote}
          rows={view.mandates}
        />
      )}

      {view.consignments.length > 0 && (
        <Group
          title={ARRANGEMENTS_COPY.groups.consignments}
          note={ARRANGEMENTS_COPY.groups.consignmentsNote}
          rows={view.consignments}
        />
      )}

      <NotHere />
    </>
  );
}

function Group({
  title,
  note,
  rows,
}: {
  title: string;
  note: string;
  rows: ArrangementRow[];
}) {
  return (
    <section className="arrangements__group">
      <div className="arrangements__group-head">
        <h2 className="arrangements__group-title">
          {title} ({rows.length})
        </h2>
        <span className="arrangements__group-note">{note}</span>
      </div>
      <ul className="arrangements__rows">
        {rows.map((row) => (
          <Row key={row.arrangementId} row={row} />
        ))}
      </ul>
    </section>
  );
}

function Row({ row }: { row: ArrangementRow }) {
  return (
    <li className="arrangements__row">
      {/* The whole row is the link, because the record is where the act to
          end this lives — beside the terms it ends. There is deliberately no
          control here; see `useArrangementsController`. */}
      <Link className="arrangements__row-open" to={row.href}>
        <WorkThumb imageUrl={row.imageUrl} title={row.title} />
        <div className="arrangements__row-body">
          <h3 className="arrangements__row-title">
            {row.title}
            {row.state && (
              <span className={`arrangements__state arrangements__state--${row.state.tone}`}>
                {row.state.label}
              </span>
            )}
          </h3>
          <p className="arrangements__row-identity">{row.identity}</p>
          <p className="arrangements__row-code">{row.ondoCode}</p>
        </div>
        <div className="arrangements__row-with">
          <span className="arrangements__row-party">{row.counterparty}</span>
          <span className="arrangements__row-when">{row.when}</span>
          {row.terms && <span className="arrangements__row-terms">{row.terms}</span>}
        </div>
        <span className="arrangements__row-action" aria-hidden="true">
          {ARRANGEMENTS_COPY.open} →
        </span>
      </Link>
    </li>
  );
}

/**
 * What this screen does not carry, and why — the activity log's device.
 *
 * Named because the rail names loans. A reader who came here for one and found
 * two headings that never mention it would reasonably assume the registry had
 * lost it; a paragraph saying there is no such record yet tells them the truth.
 */
function NotHere() {
  return (
    <section className="arrangements__nothere">
      <h2 className="arrangements__nothere-title">{ARRANGEMENTS_COPY.notHere.title}</h2>
      <ul className="arrangements__nothere-list">
        {ARRANGEMENTS_COPY.notHere.points.map((point) => (
          <li key={point}>{point}</li>
        ))}
      </ul>
    </section>
  );
}
